import React, { useRef, useState } from 'react';
import { Upload, X, Link as LinkIcon, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../../services/whatsapp/apiClient';

interface MediaUploadFieldProps {
  /** Header format, which decides the accepted file types. */
  format: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  value: string;
  onChange: (url: string) => void;
  label?: string;
  required?: boolean;
}

/** Meta's per-type caps. Rejecting locally avoids a wasted upload. */
const LIMITS: Record<string, { bytes: number; accept: string; hint: string }> = {
  IMAGE: {
    bytes: 5 * 1024 * 1024,
    accept: 'image/jpeg,image/png',
    hint: 'JPG or PNG, up to 5MB'
  },
  VIDEO: {
    bytes: 16 * 1024 * 1024,
    accept: 'video/mp4,video/3gpp',
    hint: 'MP4 or 3GP, up to 16MB'
  },
  DOCUMENT: {
    bytes: 100 * 1024 * 1024,
    accept: 'application/pdf',
    hint: 'PDF, up to 100MB'
  }
};

/**
 * Upload-or-paste field for a template media header.
 *
 * Uploads go browser → R2 via a presigned PUT, so large files never pass
 * through the API server. The returned public URL is what Meta fetches.
 */
const MediaUploadField: React.FC<MediaUploadFieldProps> = ({
  format,
  value,
  onChange,
  label,
  required
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'upload' | 'link'>('upload');
  const inputRef = useRef<HTMLInputElement>(null);

  const limit = LIMITS[format] || LIMITS.IMAGE;

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > limit.bytes) {
      setError(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is ${
          limit.bytes / 1024 / 1024
        }MB.`
      );
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Two-step: the server signs a PUT, the browser uploads straight to R2.
      const { uploadUrl, publicUrl } = await apiClient.post<{
        uploadUrl: string;
        r2Key: string;
        publicUrl: string;
      }>('/media/upload-url', {
        filename: file.name,
        mimeType: file.type,
        size: file.size,
        // Template headers must stay fetchable by Meta indefinitely, unlike
        // chat media which only needs a short-lived signed read.
        purpose: 'template'
      });

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });

      if (!response.ok) {
        throw new Error(`Upload failed with ${response.status}`);
      }

      if (!publicUrl) {
        throw new Error(
          'Upload succeeded but no public URL was returned — R2 public access may not be configured.'
        );
      }

      onChange(publicUrl);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && (
        <span className="text-[11px] font-medium text-wa-text">
          {label}
          {required && <span className="ml-0.5 text-danger-600">*</span>}
        </span>
      )}

      {value ? (
        <div className="mt-1 flex items-start gap-2 rounded-lg border border-wa-border bg-wa-surface2 p-2">
          {format === 'IMAGE' ? (
            <img
              src={value}
              alt="Header"
              className="h-16 w-16 shrink-0 rounded object-cover"
            />
          ) : (
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-wa-surface text-[10px] text-wa-muted">
              {format}
            </span>
          )}

          <span className="min-w-0 flex-1">
            <span className="block break-all text-[11px] text-wa-muted">
              {value}
            </span>
          </span>

          <button
            type="button"
            onClick={() => {
              onChange('');
              setError(null);
            }}
            className="shrink-0 rounded p-1 text-wa-muted hover:text-danger-500"
            aria-label="Remove media"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <>
          <div className="mt-1 flex gap-1">
            <button
              type="button"
              onClick={() => setMode('upload')}
              className={`flex-1 rounded-l-lg px-2 py-1 text-[11px] font-medium ${
                mode === 'upload'
                  ? 'bg-wa-green text-white'
                  : 'bg-wa-surface2 text-wa-muted'
              }`}
            >
              Upload
            </button>
            <button
              type="button"
              onClick={() => setMode('link')}
              className={`flex-1 rounded-r-lg px-2 py-1 text-[11px] font-medium ${
                mode === 'link'
                  ? 'bg-wa-green text-white'
                  : 'bg-wa-surface2 text-wa-muted'
              }`}
            >
              Paste a link
            </button>
          </div>

          {mode === 'upload' ? (
            <>
              <input
                ref={inputRef}
                type="file"
                accept={limit.accept}
                className="hidden"
                onChange={handleFile}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-wa-border p-3 text-xs text-wa-muted transition-colors hover:border-wa-green hover:text-wa-text disabled:opacity-50"
              >
                <Upload className="h-3.5 w-3.5" />
                {uploading ? 'Uploading…' : `Choose a file — ${limit.hint}`}
              </button>
            </>
          ) : (
            <div className="mt-1.5 flex items-center gap-1.5">
              <LinkIcon className="h-3.5 w-3.5 shrink-0 text-wa-muted" />
              <input
                type="url"
                onChange={(e) => onChange(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="min-w-0 flex-1 rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
              />
            </div>
          )}
        </>
      )}

      {error && (
        <p className="mt-1 flex items-start gap-1 text-[11px] text-danger-600">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export default MediaUploadField;
